// ** Modules
import { useId } from 'react';

// ** UI
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

// ** Components
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';
import {
	LightboxCloseButton,
	LightboxPrevArrow,
	LightboxNextArrow,
	LightboxDots,
	useLightboxNav
} from '@/components/common/Lightbox';

// ** Types
import type { UploadFile } from '@/types/graphql';
import type { AppContext } from '@/types/contextTypes';

export type LightboxItem = {
	src?: string;
	alt?: string;
	caption?: string;
	imageData?: UploadFile;
};

export type ImageLightboxProps = {
	open: boolean;
	items: LightboxItem[];
	activeIndex: number;
	/** Required when item `src` values are Strapi-hosted URLs. */
	host?: AppContext['host'];
	onClose: () => void;
	onPrev: () => void;
	onNext: () => void;
};

const ImageLightbox = ({ open, items, activeIndex, host, onClose, onPrev, onNext }: ImageLightboxProps) => {
	const current = items[activeIndex];
	const titleId = useId();
	const captionId = useId();

	const { hasPrev, hasNext, handleTouchStart, handleTouchEnd } = useLightboxNav({
		open,
		itemCount: items.length,
		activeIndex,
		onClose,
		onPrev,
		onNext
	});

	if (!current) return null;

	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby={titleId}
			aria-describedby={current.caption ? captionId : undefined}
			sx={{ outline: 'none' }}
			slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.95)' } } }}
		>
			<Box
				sx={{
					position: 'fixed',
					inset: 0,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					outline: 'none'
				}}
			>
				<Typography id={titleId} sx={visuallyHidden}>
					Bildansicht
				</Typography>

				{/* Desktop close button */}
				<Box
					sx={{
						display: { xs: 'none', md: 'flex' },
						justifyContent: 'flex-end',
						alignItems: 'center',
						px: '5rem',
						mb: '1.5rem'
					}}
				>
					<Box sx={{ width: 80, display: 'flex', justifyContent: 'center' }}>
						<LightboxCloseButton onClick={onClose} />
					</Box>
				</Box>

				{/* Content area: arrows + center column */}
				<Box
					onTouchStart={handleTouchStart}
					onTouchEnd={handleTouchEnd}
					sx={{
						display: 'flex',
						alignItems: 'center',
						px: { xs: '1rem', md: '5rem' }
					}}
				>
					<LightboxPrevArrow hasPrev={hasPrev} onPrev={onPrev} show={items.length > 1} label="Vorheriges Bild" />

					{/* Center column: image + caption + dots */}
					<Box
						sx={{
							flex: 1,
							display: 'flex',
							justifyContent: 'center',
							minWidth: 0
						}}
					>
						<Box
							sx={{
								display: 'inline-flex',
								flexDirection: 'column',
								maxWidth: '100%'
							}}
						>
							{/* Mobile close button */}
							<Box
								sx={{
									display: { xs: 'flex', md: 'none' },
									justifyContent: 'flex-end',
									mb: '3.25rem'
								}}
							>
								<LightboxCloseButton onClick={onClose} />
							</Box>

							{/* Image */}
							<StrapiImageRenderer
								src={current.src}
								host={host}
								alt={current.alt ?? ''}
								width={1920}
								height={1080}
								imageFit="contain"
								imageData={current.imageData}
								style={{
									maxWidth: '100%',
									maxHeight: '70vh',
									objectFit: 'contain',
									display: 'block',
									borderRadius: '0.5rem',
									width: 'auto',
									height: 'auto'
								}}
							/>

							{/* Caption */}
							{current.caption && (
								<Typography
									id={captionId}
									variant="body-sm"
									sx={{
										color: 'common.white',
										mt: { xs: '1rem', md: '1.5rem' }
									}}
								>
									{current.caption}
								</Typography>
							)}

							{/* Mobile dots */}
							<LightboxDots count={items.length} activeIndex={activeIndex} />
						</Box>
					</Box>

					<LightboxNextArrow hasNext={hasNext} onNext={onNext} show={items.length > 1} label="Naechstes Bild" />
				</Box>
			</Box>
		</Modal>
	);
};

export default ImageLightbox;
